/**
 * Role Selector Component
 * ========================
 * Single-role selection component. A user has exactly one role.
 *
 * For backend compatibility the parent form still receives the selection as a
 * `roles` array (with a single entry) plus a matching `primary_role`, so the
 * API payload shape is unchanged.
 */

import React from 'react';
import { ROLES, ROLE_LABELS, ROLE_DESCRIPTIONS, ROLE_PERMISSIONS, PERMISSION_LABELS } from '../../utils/constants';

const RoleSelector = ({ selectedRoles = [], primaryRole, onChange, onPrimaryRoleChange, disabled = false }) => {
  const availableRoles = Object.values(ROLES);
  // Only one role is ever selected; fall back to primaryRole for legacy data.
  const selectedRole = selectedRoles[0] || primaryRole || '';

  const handleRoleSelect = (role) => {
    if (disabled) return;
    if (role === selectedRole) return;

    // Keep the backend contract: a single-entry roles array + matching primary.
    onChange([role]);
    onPrimaryRoleChange(role);
  };

  return (
    <div className="space-y-4">
      {/* Role Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Select Role <span className="text-red-500">*</span>
        </label>
        <div className="space-y-2">
          {availableRoles.map((role) => {
            const isSelected = role === selectedRole;

            return (
              <div
                key={role}
                className={`border rounded-lg p-4 transition-all cursor-pointer ${
                  isSelected
                    ? 'border-primary bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => handleRoleSelect(role)}
              >
                <div className="flex items-start gap-3">
                  {/* Radio */}
                  <div className="flex items-center h-5 mt-0.5">
                    <input
                      type="radio"
                      name="role"
                      checked={isSelected}
                      onChange={() => handleRoleSelect(role)}
                      disabled={disabled}
                      className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  {/* Role Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <label className="font-medium text-gray-900 cursor-pointer">
                        {ROLE_LABELS[role]}
                      </label>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {ROLE_DESCRIPTIONS[role]}
                    </p>
                    {ROLE_PERMISSIONS[role] && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {ROLE_PERMISSIONS[role].map((perm) => (
                          <span key={perm} className="inline-block px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                            {PERMISSION_LABELS[perm] || perm}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Validation Message */}
      {!selectedRole && (
        <p className="text-sm text-red-600">
          Please select a role
        </p>
      )}
    </div>
  );
};

export default RoleSelector;
